import { writable } from 'svelte/store';
import { get_object_value_by_path } from '../lib/utils.js';
import Events from '../lib/events.js';
import { t9n } from '../lib/translate.js';
import { TS } from '../config/translation-keys.js';
import settings from '../config';
import session from '../lib/session';
import PilotPointTabs from './components/PilotPointTabs.svelte';
import BuyForm from './components/BuyForm.svelte';
import DonateForm from './components/DonateForm.svelte';
import ClaimForm from './components/ClaimForm.svelte';
import HistoryForm from './components/HistoryView.svelte';

export default class PilotPoints {
  static get component() {
    return PilotPointTabs;
  }

  constructor(init_data) {
    this.config = writable({
      ...settings,
      api_config: session.value().api_config,
      user: session.value().user,
      merchant: session.value().merchant,
      language: session.value().language,
      role: session.value().role,
    });
    this.target = init_data.target;
    this.route_name = init_data.props.route_name;
    this.query = init_data.props.query;
    this.items = null;
    this.view = null;
    this.subscriptions = [];
    this.set_context();
    this.update_state();
  }

  get invitation_code() {
    if (!!this.query && 'invitation_code' in this.query) {
      return this.query.invitation_code;
    }
    return null;
  }

  get cash_amount() {
    let pp_amount = this.config_value('user.stats.pilot_points');
    let multiplier = this.config_value('merchant.pilot_points_multiplier');

    return (pp_amount / multiplier).toFixed(2);
  }

  get page_title() {
    return t9n(TS.PILOT_POINTS_PILOTPOINTS);
  }

  get save_label() {
    return t9n(TS.PILOT_POINTS_SAVE);
  }

  get cancel_label() {
    return t9n(TS.PILOT_POINTS_CANCEL);
  }

  get buy_link_label() {
    return t9n(TS.PILOT_POINTS_BUY_LINK_LABEL);
  }

  get donate_link_label() {
    return t9n(TS.PILOT_POINTS_DONATE_LINK_LABEL);
  }

  get claim_link_label() {
    return t9n(TS.PILOT_POINTS_CLAIM_LINK_LABEL);
  }

  get history_link_label() {
    return t9n(TS.PILOT_POINTS_HISTORY_LINK_LABEL);
  }

  // manipulating config --------------------------------------------------- //

  config_value(path_str, default_value) {
    let value;

    const unsubscribe = this.config.subscribe(items => {
      value = get_object_value_by_path(path_str, items);
    });

    unsubscribe();

    return value || default_value;
  }

  // event handlers -------------------------------------------------------- //

  update_context(context) {
    this.context = { ...this.set_context(), ...context };
  }

  update_pp_amount(e) {
    let amount = e.detail.amount;

    this.config.update(items => {
      items.user.stats.pilot_points = amount;
      return { ...items };
    });

    // this.view.update(this.set_context(), this.set_items());
    this.update_state();
  }

  subscribe() {
    let owner = this;
    let handle_amount_change = e => {
      owner.update_pp_amount(e);
    };

    this.subscriptions = [Events.on('ppamountchanged', handle_amount_change)];
  }

  unsubscribe() {
    this.subscriptions.map(s => s());
  }

  // assembling ------------------------------------------------------------ //

  set_context() {
    let context = {
      config: this.config,
      route_name: this.route_name,
      query: this.query,
      invitation_code: this.invitation_code,
      user: this.config_value('user'),
      merchant: this.config_value('merchant'),
      amount: this.config_value('user.stats.pilot_points') || 0,
      pp_minimum: this.config_value('merchant.pilot_points_minimum'),
      pp_multiplier: this.config_value('merchant.pilot_points_multiplier'),
      cash_amount: this.cash_amount,
      currency_sign: this.config_value('merchant.currency_sign'),
      nets_host: this.config_value('nets_host'),
    };

    this.context = context;

    return context;
  }

  set_items() {
    let items = [
      {
        id: 'buy',
        component: BuyForm,
        link_label: this.buy_link_label,
        main: this,
        context: {},
      },
      {
        id: 'donate',
        component: DonateForm,
        link_label: this.donate_link_label,
        main: this,
        context: {},
      },
      {
        id: 'claim',
        component: ClaimForm,
        link_label: this.claim_link_label,
        main: this,
        context: {},
      },
      {
        id: 'history',
        component: HistoryForm,
        link_label: this.history_link_label,
        main: this,
        context: {},
      },
    ];

    items.map(item => {
      item.context = { ...item.context };
    });

    this.items = items;

    return items;
  }

  update_state() {
    this.set_context();
    this.set_items();
    this.render();
  }

  // lifecycle ------------------------------------------------------------- //

  render() {
    this.destroy();

    this.view = new this.constructor.component({
      target: this.target,
      props: {
        machine: this,
        app_context: this.context,
        items: this.items,
      },
    });

    this.subscribe();
  }

  destroy() {
    this.unsubscribe();

    if (!!this.view) {
      this.view.$destroy();
    }
  }
}
